import React from "react";
import { isObject } from "./isObject";

type AsyncDataLoading = {
  status: "loading";
};

type AsyncDataReady<T> = {
  status: "ready";
  data: T;
};

type AsyncDataError = {
  status: "error";
  message: string;
};

type RefObject<T> = {
  current: T;
};

export type AsyncData<D> = AsyncDataLoading | AsyncDataReady<D> | AsyncDataError;

const loadingSingleton = Object.freeze({ status: "loading" }) as AsyncDataLoading;

const loading = (): AsyncDataLoading => loadingSingleton;
const ready = <T>(data: T): AsyncDataReady<T> => ({ status: "ready", data });
const error = (message: string): AsyncDataError => ({ status: "error", message });

const isLoading = (obj: any): obj is AsyncDataLoading => isObject(obj) && obj.status === "loading";
const isReady = (obj: any): obj is AsyncDataReady<unknown> => isObject(obj) && obj.status === "ready";
const isError = (obj: any): obj is AsyncDataError => isObject(obj) && obj.status === "error";

const map = <T, U>(data: AsyncData<T>, fn: (data: T) => U): AsyncData<U> => {
  switch (data.status) {
    case "loading":
      return data;
    case "error":
      return data;
    case "ready": {
      const u = fn(data.data);
      return ready(u);
    }
  }
};

/**
 * Converts an AsyncData into a RefObject of ready data. Useful when you have a
 * ref value that you expect to be "ready", but don't want to have to check
 * on every change.
 */
const toRef = <T>(fn: () => AsyncData<T>): RefObject<T> => {
  return {
    get current() {
      const obj = fn();

      if (isError(obj)) {
        throw new Error("async.toRef: Expected ready, got error");
      }
      if (isLoading(obj)) {
        throw new Error("async.toRef: Expected ready, got loading");
      }

      return obj.data;
    },
  };
};

const equal = <T extends {}>(prev: AsyncData<T>, next: AsyncData<T>): boolean => {
  if (prev.status !== next.status) {
    return false;
  }

  if (prev.status === "error" && next.status === "error") {
    return prev.message === next.message;
  }

  if (prev.status === "ready" && next.status === "ready") {
    const prevKeys = Object.keys(prev.data);
    const nextKeys = Object.keys(next.data);

    if (prevKeys.length !== nextKeys.length) {
      return false;
    }

    for (const key of prevKeys as []) {
      if (prev.data[key] !== next.data[key]) {
        return false;
      }
    }
  }

  return true;
};

const useCachedRef = <T extends {}>(next: AsyncData<T>): AsyncData<T> => {
  const prevRef = React.useRef<AsyncData<T>>(next);
  const prev = prevRef.current;

  if (!equal(prev, next)) {
    prevRef.current = next;
  }

  return prevRef.current;
};

/**
 * Returns the collective state of many AsyncData objects. If any objects
 * are errored, then the collective state is "error". Otherwise, if any
 * objects are loading, then the collective state is "loading". Otherwise,
 * the collective status is "ready".
 */
function all<A>(objs: [AsyncData<A>]): AsyncData<[A]>;
function all<A, B>(objs: [AsyncData<A>, AsyncData<B>]): AsyncData<[A, B]>;
function all<A, B, C>(objs: [AsyncData<A>, AsyncData<B>, AsyncData<C>]): AsyncData<[A, B, C]>;
function all<A, B, C, D>(objs: [AsyncData<A>, AsyncData<B>, AsyncData<C>, AsyncData<D>]): AsyncData<[A, B, C, D]>;

function all(objs: AsyncData<any>[]): AsyncData<any[]> {
  let finalStatus: AsyncData<any>["status"] = "ready";
  const data: any[] = [];

  for (const obj of objs) {
    /**
     * If any objects are errored, then the collective state is "error".
     */
    if (obj.status === "error") {
      return obj;
    }

    /**
     * If the `finalStatus` was previously changed to `"loading"`, then
     * we only care about whether the object was an error. As we got to
     * this point, it's not an error so we can skip.
     */
    if (finalStatus === "loading") {
      continue;
    }

    /**
     * As the finalStatus is not "loading", we check whether this object
     * is loading. If it is, then we change the finalStatus to "loading".
     */
    if (obj.status === "loading") {
      finalStatus = "loading";
      continue;
    }

    /**
     * If we got to this point, then all previous objects and the current object
     * are in a ready state, so we can add the data to the collective list.
     */
    data.push(obj.data);
  }

  return finalStatus === "ready" ? ready(data) : loading();
}

export const asyncData = {
  loading,
  ready,
  error,
  map,
  all,
  equal,
  toRef,
  useCachedRef,
  is: {
    loading: isLoading,
    ready: isReady,
    error: isError,
  },
};

export type AsyncDataHelpers = typeof asyncData;
